import { FloatingFormItem, InputDecimal } from '@/components/_common';
import { QuotationSuppliersOffers } from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.types';
import {
  Col,
  Empty,
  Form,
  Input,
  Modal,
  ModalProps,
  Row,
  Select,
  Table,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { getTableScrollX } from '@/utils/table';
import { useQuotationListOffersSelect } from '@/core/domain/stock/QuotationsOffers/QuotationsOffers.hooks';
import formatterPrice from '@/utils/number/formatterPrice';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';
import formatterNumber from '@/utils/number/formatterNumber';
import formatterPercent from '@/utils/number/formatterPercent';

type Props = Omit<ModalProps, 'onCancel'> & {
  onCancel: () => void;
  suppliersSelected: QuotationSuppliersOffers[];
  hasWinnersAlreadySelected: boolean;
  quotationId?: number;
};

const ItemsSelectedListModal: React.FC<Props> = ({
  open,
  onCancel,
  suppliersSelected,
  hasWinnersAlreadySelected,
  quotationId,
}) => {
  const [form] = Form.useForm();
  const [supplierSelectId, setSupplierSelectId] = useState<number>();

  const handleCancel = () => {
    form.resetFields();
    setSupplierSelectId(undefined);
    onCancel();
  };

  const { data } = useQuotationListOffersSelect(
    hasWinnersAlreadySelected,
    quotationId,
    { rows: 50 },
  );

  const offers = flatInfiniteResult(data);

  const supplier = useMemo(() => {
    if (suppliersSelected.length) {
      return suppliersSelected.find(
        (suppliers) => suppliers.supplier.id === supplierSelectId,
      );
    }

    return offers.find((offer) => offer.supplier.id === supplierSelectId);
  }, [suppliersSelected, offers, supplierSelectId]);

  const options = offers
    .reduce((acc, item) => {
      const hasSupplier = acc.some((a) => a.supplier.id === item.supplier.id);

      if (!hasSupplier) {
        return [...acc, item];
      }

      return acc;
    }, [] as QuotationSuppliersOffers[])
    .map((item) => ({
      value: item.supplier.id,
      label: item.supplier.name,
    }));

  useEffect(() => {
    if (options.length === 1) {
      setSupplierSelectId(options[0].value);
    }
  }, [options]);

  useEffect(() => {
    if (supplier) {
      form.setFieldsValue({ ...supplier });
    }
  }, [supplier]);

  const columns: ColumnsType<QuotationSuppliersOffers> = [
    {
      key: 'itemName',
      dataIndex: 'itemName',
      title: 'Descrição',
      width: 300,
    },
    {
      key: 'manufacturer',
      dataIndex: ['manufacturer', 'name'],
      title: 'Fabricante',
      width: 300,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: 'Qtde.',
      render: (value) => formatterNumber(value),
      width: 100,
    },
    {
      key: 'deliverDays',
      dataIndex: 'deliverDays',
      title: 'Prazo',
      width: 50,
    },
    {
      key: 'discount',
      dataIndex: 'discount',
      title: '(%) Desc.',
      render: (value) => formatterPercent(value),
      width: 100,
    },
    {
      key: 'unitCost',
      dataIndex: 'unitCost',
      title: 'Valor Unit.',
      render: (value) => formatterPrice(value),
      width: 150,
    },
    {
      key: 'totalValue',
      title: 'Vl. total',
      render: (_, row) => {
        const valueTotal =
          (((row.unitCost ?? 0) * (row.quantity ?? 0)) / 100) *
          (100 - (row.discount ?? 0));

        if (valueTotal) {
          return formatterPrice(valueTotal);
        }
      },
      width: 100,
    },
  ];

  return (
    <Modal
      title={'Relação de itens selecionados para comprar por fornecedor'}
      open={open}
      footer={null}
      onCancel={handleCancel}
      width={1200}
      destroyOnClose
    >
      <Form form={form}>
        <Row gutter={[24, 24]}>
          <Col lg={18}>
            <FloatingFormItem
              name="suppliers"
              label="Fornecedor"
              initialValue={options.length === 1 ? options[0].value : undefined}
            >
              {hasWinnersAlreadySelected ? (
                <Select
                  options={options}
                  onChange={(value) => setSupplierSelectId(value)}
                />
              ) : (
                <Select
                  options={suppliersSelected
                    .filter(
                      (supplierOffer, index) =>
                        suppliersSelected
                          .map(
                            (supplierOfferItem) =>
                              supplierOfferItem.supplier.id,
                          )
                          .indexOf(supplierOffer.supplier.id) === index,
                    )
                    .map((supplierOffer) => ({
                      value: supplierOffer.supplier.id,
                      label: supplierOffer.supplier.name,
                    }))}
                  onChange={(value) => setSupplierSelectId(value)}
                />
              )}
            </FloatingFormItem>
          </Col>

          <Col lg={6}>
            <FloatingFormItem name="freightCost" label="Frete">
              <InputDecimal disabled />
            </FloatingFormItem>
          </Col>
        </Row>

        {!supplierSelectId && <Empty description="Selecione um fornecedor" />}

        {!!supplierSelectId && (
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Table
                loading={
                  hasWinnersAlreadySelected
                    ? !offers.length
                    : !suppliersSelected.length
                }
                rowKey={(row) => row.id}
                dataSource={
                  hasWinnersAlreadySelected
                    ? offers.filter(
                        (supplierOffer) =>
                          supplierOffer.supplier.id === supplierSelectId,
                      )
                    : suppliersSelected.filter(
                        (supplierOffer) =>
                          supplierOffer.supplier.id === supplierSelectId,
                      )
                }
                columns={columns}
                pagination={false}
                scroll={{ x: getTableScrollX(columns) }}
              />
            </Col>

            <Col span={24} lg={12}>
              <FloatingFormItem label="Forma pagamento" name="paymentForm">
                <Input.TextArea rows={3} disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} lg={12}>
              <FloatingFormItem label="Observações" name="observation">
                <Input.TextArea rows={3} disabled />
              </FloatingFormItem>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

export default ItemsSelectedListModal;
